import {
  type FragmentOf,
  graphql,
  type ResultOf,
  readFragment,
} from "@opensea/graphql"
import { create } from "zustand"
import { mutative } from "zustand-mutative"
import { currencyIdentifierFragment } from "@/components/currencies/utils"
import { isBulkActionEnabled } from "@/constants/constants"
import { useAcceptOffersFragment } from "../use-accept-offers.fragment"
import { useListItemsFragment } from "../use-list-items.fragment"
import { isItemTradable, isItemTradableFragment } from "../validation/tradable"
import { MAX_ITEMS_FOR_SELECTION } from "./constants"
import type { CollectionItemsActionState } from "./types"

export const collectionItemsSellSelectionFragment = graphql(
  `
    fragment collectionItemsSellSelection on Item {
      id
      imageUrl
      bestOffer {
        pricePerItem {
          token {
            unit
            symbol
            ...currencyIdentifier
          }
        }
      }
      collection {
        slug
      }
      ...useAcceptOffers
      ...useListItems
      ...isItemTradable
    }
  `,
  [
    currencyIdentifierFragment,
    useAcceptOffersFragment,
    useListItemsFragment,
    isItemTradableFragment,
  ],
)

type CollectionItemsSellSelectionState = CollectionItemsActionState & {
  // Used to hold all of the search results
  allItems: ResultOf<typeof collectionItemsSellSelectionFragment>[]
  eligibleSelectionItems: ResultOf<
    typeof collectionItemsSellSelectionFragment
  >[]
  eligibleSelectionItemsMap: Map<
    string,
    ResultOf<typeof collectionItemsSellSelectionFragment>
  >

  selectedItems: Map<
    string,
    ResultOf<typeof collectionItemsSellSelectionFragment> & {
      quantity: number
    }
  >
}

type CollectionItemsSellSelectionActions = {
  setAllItems: (
    items: FragmentOf<typeof collectionItemsSellSelectionFragment>[],
  ) => void
  // Used when the user bulk selects all items
  selectAllItems: () => void
  isItemSelectable: (itemId: string) => boolean
  getTotalSelectedQuantity: () => number
  selectItem: (itemId: string, selectionState?: boolean) => void

  setDesiredNumberOfItems: (priceInUsd: boolean, num?: number) => void
  updateSelectionQuantity: (itemId: string, quantity: number) => void
  resetSelections: () => void
  updateItemSelections: () => void
}

export type CollectionItemsSellSelectionStore =
  CollectionItemsSellSelectionState & CollectionItemsSellSelectionActions

const DEFAULT_SELL_STATE: CollectionItemsSellSelectionState = {
  allItems: [],
  eligibleSelectionItems: [],
  eligibleSelectionItemsMap: new Map(),
  mode: "automatic",
  selectedItems: new Map(),
  desiredNumberOfItems: 0,
}

export const createCollectionItemsSellSelectionStore = () => {
  return create<CollectionItemsSellSelectionStore>()(
    mutative((set, get) => ({
      ...DEFAULT_SELL_STATE,

      setDesiredNumberOfItems: (_priceInUsd: boolean, num?: number) => {
        set(state => {
          const desiredNumberOfItems = num
            ? Math.min(Math.max(0, num), MAX_ITEMS_FOR_SELECTION)
            : 0
          state.desiredNumberOfItems = desiredNumberOfItems
          if (num && state.mode === "automatic") {
            const newSelectedItems = state.eligibleSelectionItems.slice(
              0,
              desiredNumberOfItems,
            )
            state.selectedItems = new Map(
              newSelectedItems.map(item => [item.id, { ...item, quantity: 1 }]),
            )
          } else if (!num) {
            state.selectedItems = new Map()
          }
        })
      },
      setAllItems: (
        items: FragmentOf<typeof collectionItemsSellSelectionFragment>[],
      ) => {
        set(state => {
          const allItems = items.map(item =>
            readFragment(collectionItemsSellSelectionFragment, item),
          )
          state.allItems = allItems
          state.eligibleSelectionItems = allItems.filter(item =>
            isItemTradable(item),
          )

          state.eligibleSelectionItemsMap = new Map(
            state.eligibleSelectionItems.map(item => [item.id, item]),
          )
        })
      },
      isItemSelectable: (id: string) => {
        const selectedItems = get().selectedItems
        const selectedItemsCount = selectedItems.size
        const item = get().eligibleSelectionItemsMap.get(id)
        const multipleBulkActionDisabledItemsSelected =
          [...selectedItems.values()].filter(
            selectedItem => !isBulkActionEnabled(selectedItem.collection?.slug),
          ).length >= 1
        return (
          ((Boolean(item) && selectedItemsCount < MAX_ITEMS_FOR_SELECTION) ||
            selectedItems.has(id)) &&
          !multipleBulkActionDisabledItemsSelected
        )
      },
      getTotalSelectedQuantity: () => {
        return Array.from(get().selectedItems.values()).reduce(
          (total, item) => total + item.quantity,
          0,
        )
      },
      // This method should only be called when manually selecting individual items ie not sweeps
      selectItem: (id: string, selectionState?: boolean) => {
        set(state => {
          const item = state.eligibleSelectionItemsMap.get(id)

          if (
            !item ||
            (state.selectedItems.size >= MAX_ITEMS_FOR_SELECTION &&
              !state.selectedItems.has(id))
          ) {
            return
          }

          state.mode = "manual"

          if (selectionState !== undefined) {
            if (selectionState) {
              state.selectedItems.set(id, { ...item, quantity: 1 })
            } else {
              state.selectedItems.delete(id)
            }
            return
          }

          if (state.selectedItems.has(id)) {
            state.selectedItems.delete(id)
          } else {
            state.selectedItems.set(id, { ...item, quantity: 1 })
          }

          if (state.selectedItems.size === 0) {
            state.mode = "automatic"
          }
        })
      },
      selectAllItems: () => {
        set(state => {
          if (state.eligibleSelectionItems.length === 0) {
            return
          }

          if (state.selectedItems.size > 0) {
            state.selectedItems = new Map()
            state.mode = "automatic"
          } else {
            state.selectedItems = new Map(
              state.allItems
                .slice(0, MAX_ITEMS_FOR_SELECTION)
                .map(item => [item.id, { ...item, quantity: 1 }]),
            )
            state.mode = "manual"
          }
        })
      },

      updateItemSelections: () => {
        set(state => {
          if (state.mode === "automatic" && state.desiredNumberOfItems) {
            const newSelectedItems = state.eligibleSelectionItems.slice(
              0,
              state.desiredNumberOfItems,
            )
            state.selectedItems = new Map(
              newSelectedItems.map(item => [item.id, { ...item, quantity: 1 }]),
            )
          } else if (state.selectedItems.size) {
            // make sure all items in state.selectedItems still exist in state.eligibleSelectionItems
            // if not, remove them
            const itemsToRemove = Array.from(
              state.selectedItems.values(),
            ).flatMap(item => {
              if (!state.eligibleSelectionItemsMap.has(item.id)) {
                return [item.id]
              }
              return []
            })

            if (itemsToRemove.length) {
              const newSelectedItems = new Map(state.selectedItems)
              itemsToRemove.forEach(itemId => newSelectedItems.delete(itemId))
              state.selectedItems = newSelectedItems
            }
          }
        })
      },

      updateSelectionQuantity: (itemId: string, quantity: number) => {
        set(state => {
          const item = state.selectedItems.get(itemId)
          if (item) {
            if (quantity) {
              state.selectedItems.set(itemId, { ...item, quantity })
            } else {
              const currentSize = state.selectedItems.size
              state.selectedItems.delete(itemId)
              // When removing an item in sweep mode, we should switch back to manual selection mode
              if (state.mode === "automatic") {
                state.mode = "manual"
              } else if (currentSize === 1) {
                // When removing the last item in manual mode, we should switch back to automatic selection mode
                state.mode = "automatic"
                state.desiredNumberOfItems = 0
              }
            }
          }
        })
      },
      resetSelections: () => {
        set(state => {
          state.mode = "automatic"
          state.selectedItems = new Map()
          state.desiredNumberOfItems = 0
        })
      },
    })),
  )
}
